import React from 'react';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import ContentNote from '../ContentNote/ContentNote';
import { MetaInfoDescription } from './partials/MetaInfoDescription';
import { DownloadByteUnit, DownloadFileLink } from '../Download';
import { IFileItem } from '../../../types/api/FileItem';
import { DateFormat, DateFormats } from '../DateFormat';
import { Keyword } from '../Keywords/partials/Keyword';
import { PersonLink } from './partials/PersonLink';
import { isNonEmptyArray } from '../../../lib/typeGuards';
import { KeywordsMetaInfo } from './partials/KeywordsMetaInfo';
import { ICategory } from '../../../types/ICategory';

type RequiredFileItemProps = Pick<
  IFileItem,
  'editDate' | 'editedBy' | 'fileName' | 'fileExtension' | 'fileSizeBytes' | 'url'
>;
type MetaInfoFile = RequiredFileItemProps & {
  archiveDate?: string;
  author?: string;
  changeComment?: string;
  contentDate?: string;
  description?: string;
  keywords?: Keyword[];
  categories?: ICategory[];
};

/**
 * Meta Information of a FileItem.
 */
export function MetaInfoFile({
  archiveDate,
  author,
  changeComment,
  contentDate,
  description,
  editDate,
  editedBy,
  fileName,
  fileExtension,
  fileSizeBytes,
  keywords,
  categories,
  url,
}: MetaInfoFile) {
  const { t } = useSonTranslation('MetaInfo');

  return (
    <div className="meta-info">
      <dl className="meta-info__definition-list">
        <MetaInfoDescription term={t('fileName')}>
          <DownloadFileLink href={url} download>
            {fileName}
          </DownloadFileLink>
        </MetaInfoDescription>
        {description && <MetaInfoDescription term={t('description')}>{description}</MetaInfoDescription>}
        {fileExtension && <MetaInfoDescription term={t('fileExtension')}>{fileExtension}</MetaInfoDescription>}
        {fileSizeBytes && (
          <MetaInfoDescription term={t('fileSizeBytes')}>
            <DownloadByteUnit bytes={fileSizeBytes} />
          </MetaInfoDescription>
        )}
        {editDate && (
          <MetaInfoDescription term={t('editDate')}>
            <DateFormat date={editDate} format={DateFormats.DATE_TIME} />
          </MetaInfoDescription>
        )}
        {editedBy && (
          <MetaInfoDescription term={t('editedBy')}>
            <PersonLink person={editedBy} />
          </MetaInfoDescription>
        )}
        {categories && isNonEmptyArray(categories) && (
          <MetaInfoDescription term={t('categories')}>{categories.map(c => c.title).join(', ')}</MetaInfoDescription>
        )}
        {keywords && isNonEmptyArray(keywords) && <KeywordsMetaInfo keywords={keywords} term={t('keywordsOfFile')} />}
        {contentDate && (
          <MetaInfoDescription term={t('contentDate')}>
            <DateFormat date={contentDate} format={DateFormats.DATE} />
          </MetaInfoDescription>
        )}
        {author && <MetaInfoDescription term={t('author')}>{author}</MetaInfoDescription>}
        {archiveDate && (
          <MetaInfoDescription term={t('archiveDate')}>
            <DateFormat date={archiveDate} format={DateFormats.DATE} />
          </MetaInfoDescription>
        )}
        {changeComment && (
          <MetaInfoDescription term={t('changeComment')} modifierDescription="content-note">
            <ContentNote note={changeComment} />
          </MetaInfoDescription>
        )}
      </dl>
    </div>
  );
}
